import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const Login = ({ onLogin, loading, error }) => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    // Add floating label effect
    document.querySelectorAll('.form-group input').forEach(input => {
      input.addEventListener('focus', function() {
        this.parentElement.parentElement.classList.add('focused');
      });

      input.addEventListener('blur', function() {
        if (!this.value) {
          this.parentElement.parentElement.classList.remove('focused');
        }
      });
    });

    // Enhanced form validation feedback
    document.querySelectorAll('input').forEach(input => {
      const root = getComputedStyle(document.documentElement);
      const errorColor = '#ff4757';
      const borderColor = root.getPropertyValue('--color-gray-light').trim() || '#e8e8e8';
      
      input.addEventListener('invalid', function() {
        this.style.borderColor = errorColor;
        this.style.boxShadow = '0 0 0 4px rgba(255, 71, 87, 0.15)';
      });

      input.addEventListener('input', function() {
        if (this.validity.valid) {
          this.style.borderColor = borderColor;
          this.style.boxShadow = 'none';
        }
      });
    });

    // Success quotes animation
    let draggedQuote = null;
    let dragOffset = { x: 0, y: 0 };
    let isDragging = false;
    
    const quotes = [
      "Sucesso é ir de fracasso em fracasso sem perder o entusiasmo.",
      "Se você pode sonhar, pode fazer.",
      "Ação é a chave fundamental para todo sucesso.",
      "Feito é melhor que perfeito.",
      "A melhor forma de prever o futuro é criá-lo.",
      "Não encontre defeitos; encontre soluções.",
      "Oportunidades não acontecem. Você as cria.",
      "A sorte favorece a mente preparada.",
      "No meio da dificuldade está a oportunidade.",
      "A persistência é o caminho do êxito.",
      "Disciplina é a ponte entre metas e realizações.",
      "O sucesso é a soma de pequenos esforços repetidos diariamente.",
      "Comece onde você está. Use o que tem. Faça o que puder.",
      "Você perde 100% dos arremessos que não tenta.",
      "A jornada de mil milhas começa com um passo.",
      "A excelência não é um ato, é um hábito.",
      "Sorte é quando a preparação encontra a oportunidade.",
      "Acredite que você pode e você já está no meio do caminho.",
      "Foco é dizer não.",
      "Continue faminto. Continue tolo.",
      "Eu não falhei. Só achei 10.000 maneiras que não funcionam.",
      "Nossa maior fraqueza é desistir.",
      "O sucesso exige ação.",
      "Sonhe grande. Comece pequeno. Aja agora.",
      "O impossível é só opinião.",
      "Coragem é resistência ao medo, domínio do medo.",
      "O segredo de progredir é começar.",
      "Aquilo que não nos mata nos fortalece.",
      "A criatividade é inteligência se divertindo.",
      "A fortuna favorece os audazes.",
      "O sucesso depende de preparação.",
      "A persistência transforma o impossível em possível.",
      "Consistência vence intensidade.",
      "A vitória ama a preparação.",
      "A maior glória é levantar após cair.",
      "Sempre parece impossível até ser feito.",
      "O sucesso não é definitivo; o fracasso não é fatal.",
      "A melhor forma de aprender é fazer.",
      "A inovação distingue um líder de um seguidor.",
      "Trabalhe duro em silêncio; deixe o sucesso fazer barulho.",
      "A única forma de fazer um ótimo trabalho é amar o que faz.",
      "Não conte os dias; faça os dias contarem.",
      "Impossível é nada.",
      "O medo mata mais sonhos que o fracasso.",
      "A coragem é a primeira das qualidades humanas.",
      "A sorte favorece quem se prepara.",
      "Pequenos passos todo dia.",
      "Objetivos claros. Ação imediata.",
      "Sua vida só melhora quando você melhora.",
      "A motivação te inicia; o hábito te mantém.",
      "Fracasso é sucesso em progresso.",
      "Não espere. O tempo nunca será 'perfeito'.",
      "O entusiasmo move o mundo.",
      "Onde há vontade, há caminho.",
      "A melhor maneira de vencer é não parar."
    ];

    function createQuote() {
      const bgAnimation = document.getElementById('bgAnimation');
      if (!bgAnimation) return;
      
      const quote = document.createElement('div');
      quote.className = 'quote';
      const quoteText = quotes[Math.floor(Math.random() * quotes.length)];
      quote.innerHTML = '"' + quoteText + '"';
      
      // Position quotes only on the sides (left: 0-25% or right: 75-100%)
      const side = Math.random() < 0.5 ? 'left' : 'right';
      let leftPosition;
      if (side === 'left') {
        leftPosition = Math.random() * 25; // 0% to 25%
      } else {
        leftPosition = 75 + (Math.random() * 20); // 75% to 95%
      }
      quote.style.left = leftPosition + '%';
      
      const duration = 18 + Math.random() * 5; // 18-23 seconds
      quote.style.animationDuration = duration + 's';
      quote.dataset.originalDuration = duration;
      quote.dataset.animationStart = Date.now();
      
      const drift = (Math.random() - 0.5) * 30;
      quote.style.setProperty('--drift', drift + 'px');
      
      quote.addEventListener('mousedown', function(e) {
        if (e.button !== 0) return;
        const rect = quote.getBoundingClientRect();
        quote.style.animation = 'none';
        quote.style.top = rect.top + 'px';
        quote.style.left = rect.left + 'px';
        quote.classList.add('selected');
        quote.dataset.mouseDownX = e.clientX;
        quote.dataset.mouseDownY = e.clientY;
        quote.dataset.hasMoved = 'false';
        quote.dataset.isPotentialDrag = 'true';
        quote.dataset.clickTop = rect.top;
        quote.dataset.clickLeft = rect.left;
        e.preventDefault();
        e.stopPropagation();
      });
      
      bgAnimation.appendChild(quote);
      
      setTimeout(() => {
        if (quote.parentNode && !quote.dataset.manualPosition) {
          quote.parentNode.removeChild(quote);
        }
      }, duration * 1000);
    }

    // Global mouse move handler
    const handleMouseMove = (e) => {
      if (isDragging && draggedQuote) {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;
        const percentX = (newX / window.innerWidth) * 100;
        const quoteWidth = draggedQuote.offsetWidth;
        const quoteHeight = draggedQuote.offsetHeight;
        const maxX = 100 - (quoteWidth / window.innerWidth * 100);
        const constrainedX = Math.max(0, Math.min(percentX, maxX));
        const constrainedY = Math.max(0, Math.min(newY, window.innerHeight - quoteHeight));
        draggedQuote.style.left = constrainedX + '%';
        draggedQuote.style.top = constrainedY + 'px';
        return;
      }

      const allQuotes = document.querySelectorAll('.quote');
      allQuotes.forEach(function(quote) {
        if (quote.dataset.isPotentialDrag === 'true' && quote.dataset.hasMoved === 'false') {
          const mouseDownX = parseFloat(quote.dataset.mouseDownX);
          const mouseDownY = parseFloat(quote.dataset.mouseDownY);
          const deltaX = Math.abs(e.clientX - mouseDownX);
          const deltaY = Math.abs(e.clientY - mouseDownY);
          if (deltaX > 5 || deltaY > 5) {
            quote.dataset.hasMoved = 'true';
            isDragging = true;
            draggedQuote = quote;
            quote.classList.add('dragging');
            const rect = quote.getBoundingClientRect();
            dragOffset.x = e.clientX - rect.left;
            dragOffset.y = e.clientY - rect.top;
          }
        }
      });
    };

    // Global mouse up handler
    const handleMouseUp = (e) => {
      if (isDragging && draggedQuote) {
        isDragging = false;
        draggedQuote.classList.remove('dragging');
        draggedQuote.dataset.isPotentialDrag = 'false';
        draggedQuote.dataset.hasMoved = 'false';
        draggedQuote.dataset.manualPosition = 'true';
        draggedQuote = null;
      } else {
        const allQuotes = document.querySelectorAll('.quote.selected');
        allQuotes.forEach(function(quote) {
          if (quote.dataset.hasMoved === 'false') {
            quote.classList.remove('selected');
            const originalDuration = parseFloat(quote.dataset.originalDuration) || 20;
            quote.style.animation = `floatQuote ${originalDuration}s linear forwards`;
          }
        });
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // Create initial quotes
    for (let i = 0; i < 8; i++) {
      setTimeout(() => createQuote(), i * 2000);
    }

    const quoteInterval = setInterval(() => {
      createQuote();
    }, 3005);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      clearInterval(quoteInterval);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    const success = await onLogin(username, password);
    if (success) {
      navigate('/dashboard', { replace: true });
    }
  };

  return (
    <div className="login-page">
      <div className="bg-animation" id="bgAnimation"></div>

      <div className="login-container">
        <div className="logo-container">
          <div className="logo">
            <img src="/logo.png?v=2" alt="Logo do Sistema" />
          </div>
          <h1>Bem-vindo</h1>
          <p className="subtitle">Faça login para acessar o sistema</p>
        </div>

        <form method="post" id="loginForm" onSubmit={handleSubmit}>
          {error && (
            <div className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              <span>{error}</span>
            </div>
          )}
          
          <div className="form-group username-field">
            <label htmlFor="username">Usuário</label>
            <div className="input-container">
              <input
                type="text"
                id="username"
                name="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={loading}
                autoComplete="username"
              />
              <i className="fas fa-user input-icon"></i>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Senha</label>
            <div className="input-container">
              <input
                type="password"
                id="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                autoComplete="current-password"
              />
              <i className="fas fa-lock input-icon"></i>
            </div>
          </div>

          <div style={{ textAlign: 'right', marginBottom: '14px' }}>
            <Link to="/password-reset" style={{ color: 'var(--color-accent)', textDecoration: 'none', fontSize: '13px' }}>
              <i className="fas fa-key"></i> Esqueceu sua senha?
            </Link>
          </div>

          <button type="submit" className="login-btn" disabled={loading}>
            <i className="fas fa-sign-in-alt"></i>
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>

        <div className="contact-link">
          <p>Não tem uma conta?</p>
          <Link to="/signup">
            <i className="fas fa-user-plus"></i>
            Criar conta
          </Link>
        </div>

      </div>
    </div>
  );
};

export default Login;
